import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
} from "@mui/material";
import { makeStyles } from "helpers/StyleHelper";
import useGlobalContext from "hooks/globalData";
import { FormattedMessage as T } from "react-intl";
import { useQuery } from "react-query";
import {
  OrganizationUserDetails,
  ReprocessUserSubmissionsRequest,
} from "services/KnowfullyAdminClient";

type SubmissionBypassDialogProps = {
  open: boolean;
  userId: string;
  onClose: () => void;
};

const useStyles = makeStyles(() => ({
  dialogLoaderSpan: {
    marginLeft: "10px",
  },
}));

export function ReprocessSubmissionsDialog({
  open,
  onClose,
  userId,
}: SubmissionBypassDialogProps) {
  const styles = useStyles();
  const { submissionService, organizationUserService } = useGlobalContext();

  const userResult = useQuery<OrganizationUserDetails>(
    ["getOrganizationUserDetails", userId],
    () => organizationUserService.getOrganizationUserDetails(userId),
    {
      enabled: !!userId,
    }
  );

  const reprocessSubmissionsQuery = useQuery(
    ["reprocessSubmissions", userId],
    () =>
      submissionService.reprocessUserSubmissions(
        new ReprocessUserSubmissionsRequest({
          userId,
          ...userResult.data!,
        })
      ),
    {
      enabled: open && userResult.isFetched,
    }
  );

  return (
    <Dialog
      maxWidth="sm"
      fullWidth
      open={open}
      onClose={() => !reprocessSubmissionsQuery.isLoading && onClose}
    >
      <DialogTitle>
        {reprocessSubmissionsQuery.isLoading && (
          <T id="reprocessSubmissionsInProgress" />
        )}
        {reprocessSubmissionsQuery.isFetched &&
          !reprocessSubmissionsQuery.isError && (
            <T id="submissionsReprocessed" />
          )}
        {reprocessSubmissionsQuery.isError && <T id="serverError" />}
      </DialogTitle>
      <DialogContent dividers>
        {reprocessSubmissionsQuery.isLoading && (
          <T id="reprocessSubmissionsInProgressText" />
        )}
        {reprocessSubmissionsQuery.isFetched &&
          !reprocessSubmissionsQuery.isError && (
            <T id="reprocessSubmissionsSuccessText" />
          )}
        {reprocessSubmissionsQuery.isError && <T id="tryAgainLater" />}
      </DialogContent>
      <DialogActions>
        <Button
          variant="text"
          disabled={reprocessSubmissionsQuery.isLoading}
          onClick={onClose}
        >
          <T id="closeButton" />

          {reprocessSubmissionsQuery.isLoading && (
            <CircularProgress size={16} sx={styles.dialogLoaderSpan} />
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
